import { useEffect, useState } from "react"

import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import { isOnboardingFinished } from "../../../../../Utils/onboarding/isOnboardingFinished.js"
import {
  CHOICE_FLOWS,
  hasOnboardingProgress,
  resolveProductChoice
} from "../../../../../Utils/onboarding/productChoice.js"
import {
  FEATURES_STEP_KEY,
  getApplicableSteps,
  nextApplicableStepId
} from "../../../../../Utils/onboarding/stepSequence.js"
import { FEATURES_STEP_PATH, onboardingStepPath } from "../../../../../Utils/utils.jsx"
import { sendConfirmEmail as sendConfirmEmailThunk } from "../../../../LoginPage/redux/auth.thunks.js"
import { getOnboardingSteps } from "../../../utils/getOnboardingSteps.js"
import { getResendDeadline } from "../../../utils/getResendDeadline.js"
import { FEATURES_CHECKLIST_ITEM, OB_STEPS } from "../../../utils/onboardingSteps.js"

const useOnboardingStepsBlock = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const userInfo = useSelector((state) => state.auth.userInfo)
  const email = userInfo?.email
  const nextStep = userInfo?.next_step
  const confirmEmailTimeToNextTry = useSelector((state) => state.auth.confirmEmailTimeToNextTry)

  const [openModal, setOpenModal] = useState(false)
  const [initStep, setInitStep] = useState("form")
  const [resendDeadline, setResendDeadline] = useState(null)

  const totalSteps = 1

  // The backend pointer names the next step to do; once the flow is finished it sits on the last
  // step, so it is pushed one past it to read every step as done.
  const finished = isOnboardingFinished(userInfo)
  const pointer = finished ? (nextStep ?? OB_STEPS.EMAIL) + 1 : (nextStep ?? OB_STEPS.EMAIL)
  // The account's own flag is the truth; the pointer only stands in for it when the profile has
  // not been loaded with the flag yet. Nothing below the email row opens before it (OB-A-06).
  const emailVerified = userInfo?.is_email_verified ?? pointer > OB_STEPS.EMAIL

  // The product choice decides which rows exist (OB-X-01) and whether its own row is done.
  const flow = CHOICE_FLOWS.ONBOARDING
  const hasProgress = hasOnboardingProgress({ flow, userInfo })
  const choice = resolveProductChoice({ flow, hasProgress })
  const featuresDone = choice !== null
  const applicable = getApplicableSteps({ choice })
  const applicableIds = applicable.map((s) => OB_STEPS[s.step]).filter((id) => id !== undefined)

  // The step the merchant continues with: the first applicable one at or after the pointer. A
  // pointer parked on a skipped step (Recover only, backend still says "enrollment") resolves
  // forward; a finished flow has no current step at all.
  const currentId =
    pointer > OB_STEPS.TOP_UP
      ? null
      : nextApplicableStepId({ stepId: pointer, steps: applicable, stepMap: OB_STEPS })

  const backendSteps = getOnboardingSteps({ email, isEdit: true, isVerify: true }).filter(
    (s) => s.step === OB_STEPS.EMAIL || applicableIds.includes(s.step)
  )

  const featuresItem = {
    ...FEATURES_CHECKLIST_ITEM,
    isCompleted: featuresDone,
    // Features comes right after the email: it is current as soon as the email is verified and
    // stays so until a choice is recorded, and nothing after it can be current before that.
    isContinue: emailVerified && !featuresDone,
    isLocked: !emailVerified,
    // The reason is spelled out once, on the first locked row; the rest only carry the lock.
    lockedHint: !emailVerified
  }

  const withState = backendSteps.map((s) => ({
    ...s,
    isCompleted: s.step < pointer,
    isContinue: featuresDone && s.step === currentId && s.step !== OB_STEPS.EMAIL,
    isLocked: !emailVerified && s.step !== OB_STEPS.EMAIL
  }))

  // Email first, then the choice, then the backend-tracked steps; ids are display numbers.
  const [emailItem, ...rest] = withState
  const preparedSteps = [emailItem, featuresItem, ...rest].map((s, i) => ({ ...s, id: i + 1 }))

  const completedPrimarySteps = emailVerified ? 1 : 0
  const gettingStartedItems = preparedSteps.slice(1)
  const completedSecondarySteps = gettingStartedItems.filter((s) => s.isCompleted).length
  const totalSecondarySteps = gettingStartedItems.length

  const isVerifyCooldownActive = !!resendDeadline && resendDeadline > Date.now()

  const sendConfirmEmail = () => {
    if (isVerifyCooldownActive) return

    dispatch(sendConfirmEmailThunk({ email }))
  }

  const handleOpenModal = (step) => {
    if (step === "sent") {
      sendConfirmEmail()
    }
    setOpenModal(true)
    setInitStep(step)
  }
  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleResend = () => {
    sendConfirmEmail()
  }

  const handleContinue = (step) => {
    if (step === FEATURES_STEP_KEY) {
      navigate(FEATURES_STEP_PATH.onboarding)

      return
    }

    navigate(onboardingStepPath[step])
  }

  useEffect(() => {
    if (!resendDeadline) return
    const timeout = resendDeadline - Date.now()
    if (timeout <= 0) {
      setResendDeadline(null)
      return
    }
    const timerId = setTimeout(() => {
      setResendDeadline(null)
    }, timeout)
    return () => clearTimeout(timerId)
  }, [resendDeadline])

  useEffect(() => {
    if (!confirmEmailTimeToNextTry) return
    setResendDeadline(getResendDeadline(confirmEmailTimeToNextTry))
  }, [confirmEmailTimeToNextTry])

  return {
    email,
    openModal,
    initStep,
    resendDeadline,
    totalSteps,
    totalSecondarySteps,
    preparedSteps,
    handleContinue,
    handleOpenModal,
    handleCloseModal,
    handleResend,
    verifyButtonState: {
      disabled: isVerifyCooldownActive
    },
    completedPrimarySteps,
    completedSecondarySteps
  }
}

export default useOnboardingStepsBlock
